import { loadScript, waitFor } from './loadScript';
import { HEIC_EXTENSIONS, type PickedFile } from './types';

interface DropboxChooserFile {
  name: string;
  link: string;
  bytes: number;
}

interface DropboxSdk {
  choose(options: {
    success(files: DropboxChooserFile[]): void;
    cancel?(): void;
    linkType: 'direct' | 'preview';
    multiselect: boolean;
    extensions?: string[];
    folderselect?: boolean;
  }): void;
}

declare global {
  interface Window {
    Dropbox?: DropboxSdk;
  }
}

export const dropboxAppKey = import.meta.env.VITE_DROPBOX_APP_KEY as string | undefined;

export const dropboxConfigured = Boolean(dropboxAppKey);

/**
 * Dropbox Chooser. `linkType: 'direct'` returns a short-lived direct link the
 * server can fetch without any OAuth token of its own.
 */
export async function pickFromDropbox(): Promise<PickedFile[]> {
  if (!dropboxAppKey) throw new Error('Dropbox is not configured on this deployment');

  await loadScript('https://www.dropbox.com/static/api/2/dropins.js', {
    id: 'dropboxjs',
    'data-app-key': dropboxAppKey,
  });
  const sdk = await waitFor(() => window.Dropbox);

  return new Promise((resolve) => {
    sdk.choose({
      linkType: 'direct',
      multiselect: true,
      extensions: HEIC_EXTENSIONS,
      folderselect: false,
      success: (files) => {
        resolve(files.map((file) => ({ name: file.name, url: file.link, bytes: file.bytes, source: 'dropbox' })));
      },
      cancel: () => resolve([]),
    });
  });
}
